import { Can } from '@/domains/access-control';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { useCan } from '@skewb/access-control/react';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  useCreateDiscipline,
  useDeleteDiscipline,
  useDisciplinesSettings,
  useUpdateDiscipline,
} from '../hooks/useLookupSettings';

interface FormState {
  discipline: string;
  code: string;
}

const emptyForm: FormState = { discipline: '', code: '' };

export default function DisciplinesStep() {
  const { data, isLoading } = useDisciplinesSettings();
  const createMutation = useCreateDiscipline();
  const updateMutation = useUpdateDiscipline();
  const deleteMutation = useDeleteDiscipline();
  // Drop the always-empty Actions column when the role can neither edit nor delete (WRK-5669).
  const canEdit = useCan('update', 'Discipline');
  const canDelete = useCan('delete', 'Discipline');
  const showActions = canEdit || canDelete;

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const items = data?.values ?? [];
  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (i) => i.discipline?.toLowerCase().includes(q) || i.code?.toLowerCase().includes(q),
    );
  }, [items, search]);

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  }

  function openEdit(item: { id: number; discipline: string; code?: string }) {
    setEditingId(item.id);
    setForm({ discipline: item.discipline, code: item.code ?? '' });
    setDialogOpen(true);
  }

  async function handleSave() {
    const body = {
      discipline: form.discipline,
      code: form.code || undefined,
    };
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, body });
    } else {
      await createMutation.mutateAsync(body);
    }
    setDialogOpen(false);
  }

  async function handleDelete(id: number) {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirmId(null);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search disciplines..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Can action="create" subject="Discipline">
          <Button
            onClick={openCreate}
            style={{ backgroundColor: '#E84A2B' }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Discipline
          </Button>
        </Can>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Discipline</TableHead>
              <TableHead>Code</TableHead>
              {showActions && <TableHead className="w-[100px]">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={showActions ? 3 : 2}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={showActions ? 3 : 2}
                  className="text-center py-8 text-muted-foreground"
                >
                  {search ? 'No disciplines match your search' : 'No disciplines found'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.discipline}</TableCell>
                  <TableCell className="text-muted-foreground">{item.code ?? '—'}</TableCell>
                  {showActions && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Can action="update" subject="Discipline">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </Can>
                        <Can action="delete" subject="Discipline">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirmId(item.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </Can>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Discipline' : 'Add Discipline'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Discipline Name</Label>
              <Input
                placeholder="Enter discipline name"
                value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Code</Label>
              <Input
                placeholder="e.g. E002"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!form.discipline.trim() || isSaving}
              style={{ backgroundColor: '#E84A2B' }}
              className="text-white"
            >
              {isSaving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Discipline</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this discipline? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
